import { styled, TextField, IconButton, InputAdornment, SvgIcon } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material";
import { mdiClockTimeThreeOutline } from "@mdi/js";
import moment from "moment";
import { useSelector } from "react-redux";
import { generateDirStyles } from "orion-components/SharedComponents/GenerateDirStyles/GenerateDirStyles";
import { getDir } from "orion-components/i18n/Config/selector";
import { getTranslation } from "orion-components/i18n/Actions/index.js";

const CustomizedTextField = styled(TextField)(({ timeFormatPreference }) => ({
	"& .MuiOutlinedInput-notchedOutline": {
		border: "1px solid #707070",
		width: timeFormatPreference === "12-hour" ? 95 : 68,
		height: 28,
		borderRadius: 5,
		opacity: 1
	},

	"& .MuiOutlinedInput-root": {
		"&:hover fieldset": {
			borderColor: "#4DB5F4"
		},
		"&.Mui-focused fieldset": {
			borderColor: "#4DB5F4"
		},

		width: timeFormatPreference === "12-hour" ? 95 : 68,
		height: 28,
		fontWeight: "normal",
		fontFamily: "Roboto",
		letterSpacing: "0px",
		opacity: 1,
		fontSize: 12,
		color: "#B5B9BE",
		paddingRight: "8px" // Ensures space for the icon
	},

	"& .MuiInputBase-input": {
		width: timeFormatPreference === "12-hour" ? "33px" : "45px", // Ensure text input uses full space
		padding: "0 6px", // Proper padding for text visibility
		height: "100%",
		marginBottom: 4
	},

	"& .MuiInputAdornment-root": {
		margin: "0", // Ensures no extra spacing
		padding: "0"
	}
}));

const baseStyles = {
	inputAdornment: {
		"&.MuiInputAdornment-root": {
			position: "absolute !important",
			right: "0 !important"
		}
	},
	wrapper: {
		display: "flex",
		alignItems: "center",
		marginBottom: "5px"
	},
	period: {
		fontSize: 10,
		color: "#FFFFFF",
		paddingTop: "2.5px"
	},
	periodIcons: { width: 15, marginRight: "-15px" },
	timeIconWrapper: {
		backgroundColor: "grey",
		borderRadius: "4px",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		width: 22,
		height: 22,
		cursor: "pointer",
		marginLeft: 12
	},
	timeIcon: { width: 18, height: 18, color: "#FFFFFF" }
};

const formatMomentToTimeInput = (momentObj, formatPreference) => {
	if (!momentObj || !momentObj.isValid()) return { time: "00:00", period: "" }; // Default fallback

	let hours = momentObj.hours();
	const minutes = String(momentObj.minutes()).padStart(2, "0");

	if (formatPreference === "12-hour") {
		const period = hours >= 12 ? "PM" : "AM";
		hours = hours % 12 || 12; // Convert to 12-hour format
		return { time: `${String(hours).padStart(2, "0")}:${minutes}`, period };
	} else {
		return { time: `${String(hours).padStart(2, "0")}:${minutes}`, period: "" }; // 24-hour format
	}
};
const getCurrentTime = () => {
	const now = new Date();
	let hours = now.getHours();
	const minutes = String(now.getMinutes()).padStart(2, "0");
	const period = hours >= 12 ? "PM" : "AM";
	hours = hours % 12 || 12; // Convert to 12-hour format
	return { time: `${String(hours).padStart(2, "0")}:${minutes}`, period };
};

const TimeInput = ({ timePickerTime, sendValuesToParent, setShowTimePicker, error }) => {
	const [time, setTime] = useState(getCurrentTime().time);
	const [period, setPeriod] = useState(getCurrentTime().period);
	const timeFormatPreference = useSelector((state) => state.appState.global.timeFormat);
	const inputRef = useRef(null);

	const dir = useSelector((state) => getDir(state));

	const styles = generateDirStyles(baseStyles, dir);

	useEffect(() => {
		const initialTime = formatMomentToTimeInput(moment(timePickerTime), timeFormatPreference);
		setTime(initialTime?.time);
		if (timeFormatPreference === "12-hour") {
			setPeriod(initialTime?.period);
		}
	}, [timePickerTime, timeFormatPreference]);

	const handleChange = (e) => {
		const input = e.target.value.replace(/\D/g, ""); // Remove non-numeric characters
		const cursorPosition = e.target.selectionStart; // Get the current cursor position

		if (input.length > 4) return;

		let newTime = input;
		if (input.length >= 2) {
			newTime = input.slice(0, 2) + ":" + input.slice(2);
		}

		setTime(newTime);

		// After state update, restore the cursor position
		setTimeout(() => {
			if (inputRef.current) {
				let newCursorPosition = cursorPosition;

				// Adjust cursor position if a colon was added or removed
				if (newTime.length > time.length && newTime.includes(":")) {
					newCursorPosition += 1; // Move cursor forward if a colon was added
				} else if (newTime.length < time.length && !newTime.includes(":")) {
					newCursorPosition -= 1; // Move cursor backward if a colon was removed
				}

				inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
			}
		}, 0);

		const isValidTime = validateTimeInput(newTime);
		if (!isValidTime) {
			sendValuesToParent(
				"error",
				getTranslation(
					"global.CBComponents.newVideoPlayer.components.recorded.jumpToDifferentTime.components.timeInput.invalidTime"
				)
			);
		} else {
			sendValuesToParent("error", "");
		}
	};

	const synchronizeWithTimePicker = (e) => {
		if (error === "" && e?.target?.value) {
			let input = e.target.value.replace(/\D/g, ""); // Remove non-numeric characters

			if (input.length > 4) return;

			let formattedTime = input;

			if (input.length >= 2) {
				let hours = parseInt(input.slice(0, 2), 10);
				let minutes = parseInt(input.slice(2), 10) || 0; // Default minutes to 0 if not provided

				// Validate the time based on the preferred format
				if (timeFormatPreference === "12-hour") {
					if (hours < 1 || hours > 12 || minutes < 0 || minutes > 59) return;
					formattedTime = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
				} else {
					if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return;
					formattedTime = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
				}
			}
			sendValuesToParent("time", moment(formattedTime, "HH:mm").toDate());
		}
	};
	// Function to toggle AM/PM
	const togglePeriod = () => {
		setPeriod((prev) => (prev === "AM" ? "PM" : "AM"));
	};

	const validateTimeInput = (input) => {
		if (!input) return false;

		// Remove non-numeric characters except ':'
		const cleanedInput = input.replace(/[^\d:]/g, "");

		// Ensure the format is HH:MM
		const timeRegex =
			timeFormatPreference === "12-hour"
				? /^(0[1-9]|1[0-2]):([0-5][0-9])$/ // 12-hour format (01:00 - 12:59)
				: /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/; // 24-hour format (00:00 - 23:59)

		return timeRegex.test(cleanedInput);
	};

	return (
		<CustomizedTextField
			value={time}
			onChange={handleChange}
			onBlur={synchronizeWithTimePicker}
			timeFormatPreference={timeFormatPreference}
			inputProps={{
				autoComplete: "off",
				ref: inputRef
			}}
			placeholder="hh:mm"
			InputProps={{
				endAdornment: (
					<InputAdornment position="end" sx={styles.inputAdornment}>
						<div style={styles.wrapper}>
							{timeFormatPreference === "12-hour" && (
								<>
									<span style={styles.period}>{period}</span>
									<IconButton size="small" onClick={togglePeriod} style={{ padding: 0 }} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-jump-to-different-time-components-time-input-icon-button">
										{period === "AM" ? (
											<ArrowDropUp sx={styles.periodIcons} />
										) : (
											<ArrowDropDown sx={styles.periodIcons} />
										)}
									</IconButton>
								</>
							)}
							<div style={styles.timeIconWrapper}>
								<SvgIcon
									style={styles.timeIcon}
									onClick={() => {
										setShowTimePicker(true);
										synchronizeWithTimePicker();
									}} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-jump-to-different-time-components-time-input-svg-icon"
								>
									<path d={mdiClockTimeThreeOutline} />
								</SvgIcon>
							</div>
						</div>
					</InputAdornment>
				)
			}} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-jump-to-different-time-components-time-input-customized-text-field"
		/>
	);
};

export default TimeInput;
